import { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp
} from "firebase/firestore";

import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function Projects() {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'Active',
    start_date: '',
    end_date: '',
  });
  const [selectedDevelopers, setSelectedDevelopers] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    setUser(storedUser);
    if (storedUser) {
      fetchProjects(storedUser);
      if (storedUser.role !== 'client') fetchUsers();
    }
  }, []);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));

    const isAuthorized =
      storedUser &&
      (storedUser.role === "admin" || storedUser.role === "super_admin");

    if (!isAuthorized) {
      toast.error("Our AI monitoring system has automatically recorded this event.");
      navigate("/dashboard");
      return;
    }

    setUser(storedUser);
    if (storedUser) fetchUsers(storedUser);
  }, []);
  // Fetch projects from Firebase
const fetchProjects = async (currentUser) => {
  try {
    setLoading(true);
    let q;

    if (currentUser.role === 'super_admin') {
      q = query(collection(db, 'projects'), orderBy('created_at', 'desc'));

    } else if (currentUser.role === 'admin') {
      // Admin sees ALL PROJECTS
      q = query(collection(db, 'projects'), orderBy('created_at', 'desc'));

    } else if (currentUser.role === 'developer') {
      q = query(
        collection(db, 'projects'),
        where('assigned_developers', 'array-contains', currentUser.id),
        orderBy('created_at', 'desc')
      );

    } else if (currentUser.role === 'client') {
      q = query(
        collection(db, 'projects'),
        where('assigned_clients', 'array-contains', currentUser.id),
        orderBy('created_at', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setProjects(data);

  } catch (error) {
    console.error(error);
    toast.error('Failed to load projects');
  } finally {
    setLoading(false);
  }
};


  // Fetch all users (for assigning)
  const fetchUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllUsers(data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  // Create new project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      await addDoc(collection(db, 'projects'), {
        ...formData,
        status: formData.status,
        organization_id: user.organization_id || '',
        assigned_developers: [],
        assigned_clients: [],
        created_at: serverTimestamp(),
      });
      toast.success('Project created successfully!');
      setDialogOpen(false);
      setFormData({ name: '', description: '', status: 'Active', start_date: '', end_date: '' });
      fetchProjects(user);
    } catch (error) {
      console.error(error);
      toast.error('Failed to create project');
    }
  };

  // Assign developers & clients
  const handleAssignUsers = async () => {
    if (!selectedProject) return;
    try {
      const projectRef = doc(db, 'projects', selectedProject.id);
      await updateDoc(projectRef, {
        assigned_developers: selectedDevelopers,
        assigned_clients: selectedClients
      });
      toast.success('Users assigned successfully!');
      setAssignDialogOpen(false);
      fetchProjects(user);
    } catch (error) {
      console.error(error);
      toast.error('Failed to assign users');
    }
  };

  const openAssignDialog = (project) => {
    setSelectedProject(project);
    setSelectedDevelopers(project.assigned_developers || []);
    setSelectedClients(project.assigned_clients || []);
    setAssignDialogOpen(true);
  };

  if (!user) return <div>Loading...</div>;

  return (
    <Layout user={user}>
      <div data-testid="projects-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Projects</h1>
            <p className="text-gray-600 mt-2">Manage your projects and team assignments</p>
          </div>
{console.log("userrrrrr0" ,user)}
          {(user.role.toLowerCase() == 'super_admin') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
     <Button data-testid="create-project-btn">
                  <Plus className="mr-2 h-4 w-4" /> New Project
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Project</DialogTitle>
                  <DialogDescription>Add a new project to your organization</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4">
                  <div>
                    <Label>Project Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">Create Project</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">Loading projects...</div>
        ) : projects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No projects found. Create your first project!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl">{project.name}</CardTitle>
                    <Badge variant={project.status === 'Active' ? 'default' : 'secondary'}>
                      {project.status}
                    </Badge>
                  </div>
                  <CardDescription className="line-clamp-2">{project.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="mr-2 h-4 w-4" />
                    {project.start_date} - {project.end_date}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Users className="mr-2 h-4 w-4" />
                    {project.assigned_developers?.length || 0} Developers, {project.assigned_clients?.length || 0} Clients
                  </div>
                  {(user.role === 'super_admin' || user.role === 'admin') && (
                    <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => openAssignDialog(project)}>
                      Assign Users
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Assign Users to Project</DialogTitle>
              <DialogDescription>{selectedProject?.name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Developers</Label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {allUsers.map(dev => (
                    <label key={dev.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDevelopers.includes(dev.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedDevelopers([...selectedDevelopers, dev.id]);
                          else setSelectedDevelopers(selectedDevelopers.filter(id => id !== dev.id));
                        }}
                      />
                      <span>{dev.name} ({dev.email})</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Clients</Label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {allUsers.filter(u => u.role === 'client').map(client => (
                    <label key={client.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedClients([...selectedClients, client.id]);
                          else setSelectedClients(selectedClients.filter(id => id !== client.id));
                        }}
                      />
                      <span>{client.name} ({client.email})</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={handleAssignUsers} className="w-full">
                Save Assignments
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
